// client/src/components/pages/OperationOverview/executiveFigures.ts (converted from .js, ST-00216)
//
// Headline figures for the Executive Summary tab (ST-00215, RSO-7), from the
// `summary` block of GET /route-summary/executive-summary.
//
// The server builds them with the same per-route computation as the Route
// Detail cards and headline: `totalDispatchWeight` is Dispatched after Brought
// Forward / Carried Forward, so "Dispatched" and the net variance here equal
// the Route Detail tiles for the same period. The daily chart shows weighed
// tonnage; `weighedDispatchWeight` is its dispatch total, and
// weighed + BF - CF = Dispatched.
//
// Nothing here adds dispatched and received tonnage together — a load is
// dispatched once and received once, so that sum counted every load twice
// (the old "MT total" and "Daily Throughput: MT combined avg").
//
// ST-00218 P3: on "Truck + time" routes Dispatched also deducts the loads received off-route
// and those without receipt data (weighed + BF − CF − off-route − no receipt data); missing
// and needs-review loads stay in it (they are named, not deducted).
//
// ST-00298: the net variance needs a dispatch over 0.5 t (utils/variance), and it leaves out the
// shown routes that received against nothing left dispatched (every dispatch carried forward):
// their tonnes stay in Dispatched and Received, but would read as an over-delivery.

import type {
  ExecutiveSummaryFigures,
  OverviewGrouping,
} from '../../../types/clientPages/operationOverview';
import { hasVarianceBase, lacksVarianceBase } from '../../../utils/variance';
import { adjustedDispatch, lacksDispatchBase } from './overviewKpis';

export interface ExecutiveFigures {
  dispatched: number;
  received: number;
  /** Net variance (t): received − dispatched, without the routes left out (ST-00298). */
  difference: number;
  /** difference / dispatched in %, without the routes left out; null = n/a. */
  variancePct: number | null;
  /** ST-00298: shown routes left out of the net variance (received, nothing left dispatched). */
  varianceExcludedRoutes: number;
  hasBfcf: boolean;
  broughtForward: number;
  carriedForward: number;
  bfcfNet: number;
  /** ST-00218 P3 (Truck + time routes): deducted from Dispatched like CF. */
  receivedOffRoute: number;
  noReceiptData: number;
  /** received off-route or no receipt data > 0. */
  hasDeductions: boolean;
  /** BF − CF − received off-route − no receipt data: weighed + this = Dispatched. */
  adjustmentNet: number;
  /** Included in Dispatched (not deducted). */
  missingWeight: number;
  missingCount: number;
  needsReviewWeight: number;
  needsReviewCount: number;
  weighedDispatched: number;
  avgDailyDispatch: number;
  avgDailyReceipt: number;
  dispatchShare: number;
  routeCount: number | null;
}

const num = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * @param summary - the `summary` block of GET /route-summary/executive-summary
 * @param routes - the route cards (GET /route-summary/groupings): those of the summary's
 *   `groupingIds` that lack a dispatch base are left out of the net variance
 */
export const executiveFigures = (
  summary: ExecutiveSummaryFigures = {},
  routes: ReadonlyArray<OverviewGrouping> = []
): ExecutiveFigures => {
  const dispatched = num(summary.totalDispatchWeight);
  const received = num(summary.totalReceiptWeight);
  const totalDifference =
    summary.totalDifference !== undefined && summary.totalDifference !== null
      ? num(summary.totalDifference)
      : received - dispatched;
  // ST-00298: out of the net variance, whole — the summary's routes that lack a dispatch base.
  const shownIds = new Set((summary.groupingIds || []).map(Number));
  const noBase = routes.filter((g) => shownIds.has(Number(g.id)) && lacksDispatchBase(g));
  const outD = noBase.reduce((sum, g) => sum + adjustedDispatch(g), 0);
  const outR = noBase.reduce((sum, g) => sum + (g.totalReceiptWeight || 0), 0);
  const base = dispatched - outD;
  const difference = totalDifference - (outR - outD);
  const variancePct =
    noBase.length > 0 && noBase.length >= shownIds.size
      ? null // every route left out
      : hasVarianceBase(base)
        ? (difference / base) * 100
        : lacksVarianceBase(received - outR, base)
          ? null
          : 0;
  const hasBfcf =
    summary.broughtForwardWeight !== undefined || summary.carriedForwardWeight !== undefined;
  const broughtForward = num(summary.broughtForwardWeight);
  const carriedForward = num(summary.carriedForwardWeight);
  const weighedDispatched =
    summary.weighedDispatchWeight !== undefined ? num(summary.weighedDispatchWeight) : dispatched;
  const receivedOffRoute = num(summary.receivedOffRouteWeight);
  const noReceiptData = num(summary.noReceiptDataWeight);
  return {
    dispatched,
    received,
    difference,
    variancePct,
    varianceExcludedRoutes: noBase.length,
    hasBfcf,
    broughtForward,
    carriedForward,
    // Net BF/CF adjustment between the weighed chart total and Dispatched.
    bfcfNet: broughtForward - carriedForward,
    receivedOffRoute,
    noReceiptData,
    hasDeductions: receivedOffRoute > 0 || noReceiptData > 0,
    adjustmentNet: broughtForward - carriedForward - receivedOffRoute - noReceiptData,
    missingWeight: num(summary.missingWeight),
    missingCount: num(summary.missingCount),
    needsReviewWeight: num(summary.needsReviewWeight),
    needsReviewCount: num(summary.needsReviewCount),
    weighedDispatched,
    // Average weighed dispatch per day with data (the chart's daily mean).
    avgDailyDispatch: num(summary.avgDailyDispatch),
    avgDailyReceipt: num(summary.avgDailyReceipt),
    // Share of the split bar — a ratio, not a total.
    dispatchShare: dispatched + received > 0 ? (dispatched / (dispatched + received)) * 100 : 50,
    routeCount:
      summary.routeCount !== undefined && summary.routeCount !== null
        ? num(summary.routeCount)
        : null,
  };
};

/** Formats a tonnage for the sentences below (the page passes its en-ZA whole-tonne format). */
type Fmt = (value: number) => string;

/**
 * "Dispatched X MT = weighed W + brought forward B − carried forward C MT", and on "Truck +
 * time" routes "… − received off-route O − no receipt data N MT" (each only when > 0).
 * null for an older summary without BF/CF fields.
 */
export const dispatchedExplanation = (f: ExecutiveFigures, fmt: Fmt): string | null => {
  if (!f.hasBfcf) return null;
  const parts = [
    `Dispatched ${fmt(f.dispatched)} MT = weighed ${fmt(f.weighedDispatched)}`,
    `+ brought forward ${fmt(f.broughtForward)}`,
    `− carried forward ${fmt(f.carriedForward)}`,
  ];
  if (f.receivedOffRoute > 0) parts.push(`− received off-route ${fmt(f.receivedOffRoute)}`);
  if (f.noReceiptData > 0) parts.push(`− no receipt data ${fmt(f.noReceiptData)}`);
  return `${parts.join(' ')} MT`;
};

const loads = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * The missing and needs-review loads Dispatched includes (SPEC Q4: they stay in it, so the
 * variance shows them), e.g. "Dispatched includes 3 missing loads (118 MT)." null when none.
 */
export const includedLossesText = (f: ExecutiveFigures, fmt: Fmt): string | null => {
  const parts: string[] = [];
  if (f.missingCount > 0)
    parts.push(
      `${loads(f.missingCount, 'missing load', 'missing loads')} (${fmt(f.missingWeight)} MT)`
    );
  if (f.needsReviewCount > 0)
    parts.push(
      `${loads(f.needsReviewCount, 'load that needs review', 'loads that need review')} (${fmt(f.needsReviewWeight)} MT)`
    );
  return parts.length ? `Dispatched includes ${parts.join(' and ')}.` : null;
};

export default executiveFigures;
